import React, { memo, useCallback, useState } from 'react'

import {
  AIGenerated2,
  Button,
  Icon,
  Verified,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func, string } from 'prop-types'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import i18n from 'Pods/Shared/I18nThreeDStudio'

import { NOOP } from 'Utils/stableDefaults'

import AccessCodeForm from '../AccessCodeForm/AccessCodeForm'

const WAITLIST_PATH = '/api/internal/generator/waitlist'

// Free-beta card shared by the 3D Studio usage modal and the AI subscription landing
// waitlist modal. The component owns the join POST and the access-code reveal; callers
// supply onJoined (apply the result -- the studio writes the new tier to its store, the
// landing swaps to its confirmation view) and onCodeApplied (forwarded to AccessCodeForm).
// locale is passed in (not read from context) so the component stays usable outside the
// studio page, where ThreeDStudioPageContext is absent.
function WaitlistCard({ onJoined = NOOP, onCodeApplied = NOOP, onClose: handleClose, locale = 'en' }) {
  const { postData: postWaitlist, isLoading: isJoining } = useApiPost()
  const [isCodeFormOpen, setIsCodeFormOpen] = useState(false)

  const t = useCallback(
    (key, options) => i18n.t(key, { ns: 'three-d-studio', lng: locale, ...options }),
    [locale],
  )

  const handleJoin = useCallback(() => {
    if (isJoining) {
      return
    }

    postWaitlist({
      path: WAITLIST_PATH,
      data: {},
      onSuccess: (response) => {
        const data = response?.data?.data || {}

        onJoined(data)
      },
      onError: () => {
        window.showNotification(t('waitlist.card.joinError'), { type: 'error', autohide: true })
      },
    })
  }, [postWaitlist, isJoining, onJoined, t])

  const handleToggleCodeForm = useCallback(() => {
    setIsCodeFormOpen(prev => !prev)
  }, [])

  return (
    <section className="usage-modal__card usage-modal__card--beta">
      <div className="usage-modal__card-chip cgt-caption-semibold usage-modal__card-chip--beta">
        {t('waitlist.card.chip')}
      </div>

      <div className="usage-modal__price">
        <span className="cgt-heading-300">$0</span>
      </div>

      <ul className="usage-modal__bullets">
        <li className="usage-modal__bullet">
          <Icon
            svgComponent={Verified}
            color="primary"
            size="small"
            notSpaced
            className="usage-modal__bullet-icon"
          />
          <span className="cgt-body-base">
            <span className="cgt-body-md-semibold">{t('waitlist.card.freeGenerationsHighlight')}</span>
            {t('waitlist.card.freeGenerationsSuffix')}
          </span>
        </li>
        <li className="usage-modal__bullet">
          <Icon
            svgComponent={Verified}
            color="primary"
            size="small"
            notSpaced
            className="usage-modal__bullet-icon"
          />
          <span className="cgt-body-base">{t('waitlist.card.noCreditCard')}</span>
        </li>
        <li className="usage-modal__bullet-divider" aria-hidden="true" />
        <li className="usage-modal__bullet">
          <Icon
            svgComponent={AIGenerated2}
            color="primary"
            size="small"
            notSpaced
            className="usage-modal__bullet-icon"
          />
          <span className="cgt-body-base">{t('waitlist.card.gettingStarted')}</span>
        </li>
      </ul>

      <div className="usage-modal__card-footer">
        <Button
          size="small"
          type="secondary"
          onClick={handleJoin}
          disabled={isJoining}
        >
          {isJoining ? t('waitlist.card.joining') : t('waitlist.card.join')}
        </Button>
        <Button
          size="small"
          type="ghost"
          onClick={handleToggleCodeForm}
        >
          {t('waitlist.card.haveAccessCode')}
        </Button>
      </div>

      {isCodeFormOpen && <AccessCodeForm onApplied={onCodeApplied} onClose={handleClose} locale={locale} />}
    </section>
  )
}

WaitlistCard.propTypes = {
  onJoined: func,
  onCodeApplied: func,
  onClose: func.isRequired,
  locale: string,
}

export default memo(WaitlistCard)
